import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconSearch} from '@tabler/icons-react';
import type {CloudWatchLogGroupResponse} from 'src/api/ProactApiClient';
import {CloudWatchLogLevel} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Textbox} from 'src/design/atoms/Textbox';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import type {ISelectOption} from 'src/types/Form';
import {
  LOG_LEVEL_OPTIONS,
  LOG_RANGE_OPTIONS,
  formatRetention,
  formatStoredBytes,
} from '../cloudWatchDisplay';
import {useCloudWatchLogSearch} from '../useCloudWatchLogSearch';
import {CloudWatchLogsTable} from './CloudWatchLogsTable';

export interface CloudWatchLogsPanelProps {
  groups: CloudWatchLogGroupResponse[];
  groupsLoading: boolean;
  groupsError: boolean;
  groupsErrorMessage?: string;
}

export const CloudWatchLogsPanel = ({
  groups,
  groupsLoading,
  groupsError,
  groupsErrorMessage,
}: CloudWatchLogsPanelProps) => {
  const search = useCloudWatchLogSearch(groups);

  const groupOptions: ISelectOption[] = groups.map(g => ({
    id: g.name,
    label: `${g.label} — ${formatRetention(
      g.retentionDays
    )}, ${formatStoredBytes(g.storedBytes)}`,
  }));

  if (groupsError) {
    return (
      <ErrorAlert
        message={groupsErrorMessage ?? 'Failed to load log groups.'}
      />
    );
  }

  if (groupsLoading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (groups.length === 0) {
    return (
      <NoItemsPanel noItemsText="No ECS log groups were found for this environment." />
    );
  }

  return (
    <Stack gap="md">
      <SectionCard title="Query">
        <Box p="md">
          <Group align="flex-end" gap="md" wrap="wrap">
            <Box w={320}>
              <SelectBox
                label="Log group"
                ariaLabel="Log group"
                placeholder="Select a log group"
                options={groupOptions}
                value={search.group}
                onSelectChangeCallback={search.setGroup}
              />
            </Box>
            <Box w={180}>
              <SelectBox
                label="Level"
                ariaLabel="Log level"
                placeholder="All levels"
                options={LOG_LEVEL_OPTIONS}
                value={String(search.level)}
                onSelectChangeCallback={value =>
                  search.setLevel(
                    value === ''
                      ? CloudWatchLogLevel.All
                      : (Number(value) as CloudWatchLogLevel)
                  )
                }
              />
            </Box>
            <Box mb="md">
              <Text size="sm" fw={500} mb={4}>
                Range
              </Text>
              <SegmentedControl
                value={String(search.minutes)}
                onChange={value => search.setMinutes(Number(value))}
                data={LOG_RANGE_OPTIONS}
                aria-label="Log range"
              />
            </Box>
            <Box style={{flex: 1, minWidth: 260}}>
              <Textbox
                label="Filter"
                ariaLabel="Filter text or CloudWatch pattern"
                placeholder="Filter text or CloudWatch pattern"
                value={search.text}
                onChangeCallback={event => search.setText(event.target.value)}
                onEnterCallback={search.search}
              />
            </Box>
            <Box mb="sm">
              <Button
                size="sm"
                leftSection={<IconSearch size={16} />}
                loading={search.isFetching}
                onClick={search.search}>
                Search
              </Button>
            </Box>
          </Group>
        </Box>
      </SectionCard>

      {search.searchError && <ErrorAlert message={search.searchError} />}

      <CloudWatchLogsTable
        events={search.events}
        isFetching={search.isFetching}
        isError={search.searchError !== undefined}
        error={search.searchError}
        hasSearched={search.hasSearched}
      />

      {search.hasMore && (
        <Group justify="center">
          <Button
            size="sm"
            variant="subtle"
            color="gray"
            loading={search.isFetching}
            onClick={search.loadMore}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
};
